import React, { useMemo, useState, useEffect, useRef } from "react";
import { Box, Paper, CircularProgress, Stack } from "@mui/material";
import { Rnd } from "react-rnd";

const ALIGN_THRESHOLD = 1;

const ImageElement = ({ element, updateElement, canvasDimensions }) => {
  const handleLoad = (e) => {
    const img = e.target;
    const imgW = img.naturalWidth;
    const imgH = img.naturalHeight;

    if (!imgW || !imgH) return;

    // Calculate shrink factor to fit inside current box or max canvas
    const maxWidth = canvasDimensions?.width || 1920;
    const maxHeight = canvasDimensions?.height || 1080;

    // Optional: you can also limit by some default box size if element.width/height exist
    const boxWidth = element.width || maxWidth / 2;
    const boxHeight = element.height || maxHeight / 2;

    const widthRatio = boxWidth / imgW;
    const heightRatio = boxHeight / imgH;
    const shrinkFactor = Math.min(widthRatio, heightRatio, 1); // never scale up

    const newWidth = Math.floor(imgW * shrinkFactor);
    const newHeight = Math.floor(imgH * shrinkFactor);

    // Update element size
    updateElement(
      element.id,
      { width: Math.round(newWidth), height: Math.round(newHeight) },
      false
    );
  };

  return (
    <img
      src={element.src}
      alt="Canvas element"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      }}
      onLoad={handleLoad}
      onDragStart={(e) => e.preventDefault()}
    />
  );
};

const applyTextCase = (text, textCase) => {
  if (!text) return text;
  if (textCase === "uppercase") return text.toUpperCase();
  if (textCase === "lowercase") return text.toLowerCase();
 if (textCase === "sentence")
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  return text;
};

const TextElement = ({ element, dataBindingValue }) => {
  const rawContent = element.originalContent || element.content || "";
  const actualContent = applyTextCase(rawContent, element.textCase);
  const actualFontSize = element.originalFontSize || element.fontSize || 16;

  const horizontalAlign =
    element.textAlign === "center"
      ? "center"
      : element.textAlign === "right"
      ? "flex-end"
      : "flex-start";

  const verticalAlign =
    element.verticalAlign === "top"
      ? "flex-start"
      : element.verticalAlign === "bottom"
      ? "flex-end"
      : "center";

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: verticalAlign,
        justifyContent: horizontalAlign,
        textAlign: element.textAlign,
        color: element.fill || element.color,
        fontSize: `${actualFontSize}px`,
        fontFamily: element.fontFamily,
        fontWeight: element.fontWeight,
        fontStyle: element.fontStyle,
        padding: "5px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "pre-wrap",
        lineHeight: 1.2,
        boxSizing: "border-box",
        minWidth: 0,
        minHeight: 0,
        maxWidth: "100%",
        maxHeight: "100%",
        wordWrap: "break-word",
      }}
    >
      {actualContent}
    </div>
  );
};

const ShapeElement = ({ element }) => (
  <div
    style={{
      width: "100%",
      height: "100%",
      borderRadius: element.shapeType === "circle" ? "50%" : "0",
      backgroundColor: element.fill,
    }}
  />
);

const QRCodeElement = ({ element, updateElement }) => {
  const handleLoad = (e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    if (element.width !== element.height) {
      const size = Math.min(element.width, element.height);
      updateElement(element.id, {
        width: size,
        height: size,
      });
    } else if (aspectRatio !== 1) {
      const size = Math.min(img.naturalWidth, img.naturalHeight);
      updateElement(element.id, {
        width: size,
        height: size,
      });
    }
  };

  return (
    <img
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${element.width}x${
        element.height
      }&data=${element.url}&color=${element.fill.substring(1)}`}
      alt="QR Code"
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        display: "block",
      }}
      onLoad={handleLoad}
      onDragStart={(e) => e.preventDefault()}
    />
  );
};

const LineElement = ({ element }) => {
  const innerLineStyle = {
    width: element.direction === "horizontal" ? "100%" : "2px",
    height: element.direction === "vertical" ? "100%" : "2px",
    backgroundColor: element.fill || "#000000",
    border: "none",
  };
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <hr style={innerLineStyle} />
    </div>
  );
};

const IconsElement = () => (
  <div
    style={{
      width: "100%",
      height: "100%",
      border: "2px dashed #888",
      borderRadius: 4,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      backgroundColor: "rgba(136,136,136,0.08)",
      boxSizing: "border-box",
    }}
  >
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 12,
            height: 12,
            borderRadius: 2,
            backgroundColor: "#888",
            opacity: 0.5,
          }}
        />
      ))}
    </div>
    <span style={{ fontSize: 10, color: "#888", letterSpacing: 1 }}>ICONS</span>
  </div>
);

export default function Canvas({
  elements,
  selectedElement,
  setSelectedElement,
  updateElement,
  canvasRef,
  resolution,
  isLoadingState,
  canvasDimensions,
  dataBindingValue,
  templateMode,
}) {
  const [guides, setGuides] = useState([]);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  const { originalWidth, originalHeight } = useMemo(() => {
    if (!resolution) return { originalWidth: 1920, originalHeight: 1080 };
    const [w, h] = resolution.split("x").map(Number);
    return { originalWidth: w || 1920, originalHeight: h || 1080 };
  }, [resolution]);

  useEffect(() => {
    const updateScale = () => {
      if (
        !containerRef.current ||
        !canvasDimensions?.width ||
        !canvasDimensions?.height
      ) {
        setScale(1);
        return;
      }

      const { clientWidth, clientHeight } = containerRef.current;
      if (!clientWidth || !clientHeight) {
        setScale(1);
        return;
      }

      // Account for padding (p: 3 = 24px on each side = 48px total)
      const padding = 48;
      const availableWidth = clientWidth - padding;
      const availableHeight = clientHeight - padding;

      if (availableWidth <= 0 || availableHeight <= 0) {
        setScale(1);
        return;
      }

      const scaleX = availableWidth / canvasDimensions.width;
      const scaleY = availableHeight / canvasDimensions.height;
      const nextScale = Math.min(scaleX, scaleY, 1); // never upscale, only downscale

      setScale(nextScale > 0 ? nextScale : 1);
    };

    updateScale();
    const resizeObserver = new ResizeObserver(updateScale);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    window.addEventListener("resize", updateScale);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [canvasDimensions]);

  const getEdges = (el) => ({
    left: el.x,
    right: el.x + el.width,
    top: el.y,
    bottom: el.y + el.height,
    centerX: el.x + el.width / 2,
    centerY: el.y + el.height / 2,
  });

  const handleDrag = (id, e, d) => {
    const moving = elements.find((el) => el.id === id);
    if (!moving) return;

    const movingEdges = getEdges({ ...moving, x: d.x, y: d.y });

    let newGuides = [];

    elements.forEach((el) => {
      if (el.id === id) return;

      const edges = getEdges(el);

      [
        ["left", "left"],
        ["centerX", "centerX"],
        ["right", "right"],
      ].forEach(([a, b]) => {
        if (Math.abs(movingEdges[a] - edges[b]) < ALIGN_THRESHOLD) {
          newGuides.push({ type: "vertical", position: edges[b] });
        }
      });

      [
        ["top", "top"],
        ["centerY", "centerY"],
        ["bottom", "bottom"],
      ].forEach(([a, b]) => {
        if (Math.abs(movingEdges[a] - edges[b]) < ALIGN_THRESHOLD) {
          newGuides.push({ type: "horizontal", position: edges[b] });
        }
      });
    });

    // Show guides but don't snap while dragging
    setGuides(newGuides);
    // Move element freely with the cursor (no snapping) and don't spam history
    updateElement(id, { x: d.x, y: d.y }, false);
  };

  const handleDragStop = (id, e, d) => {
    const moving = elements.find((el) => el.id === id);
    if (!moving) {
      setGuides([]);
      return;
    }

    const movingEdges = getEdges({ ...moving, x: d.x, y: d.y });

    let snapX = d.x;
    let snapY = d.y;

    elements.forEach((el) => {
      if (el.id === id) return;

      const edges = getEdges(el);

      [
        ["left", "left"],
        ["centerX", "centerX"],
        ["right", "right"],
      ].forEach(([a, b]) => {
        if (Math.abs(movingEdges[a] - edges[b]) < ALIGN_THRESHOLD) {
          snapX += edges[b] - movingEdges[a];
        }
      });

      [
        ["top", "top"],
        ["centerY", "centerY"],
        ["bottom", "bottom"],
      ].forEach(([a, b]) => {
        if (Math.abs(movingEdges[a] - edges[b]) < ALIGN_THRESHOLD) {
          snapY += edges[b] - movingEdges[a];
        }
      });
    });

    // Clear guides and apply a single snap at the end of the drag
    setGuides([]);
    updateElement(id, { x: Math.round(snapX), y: Math.round(snapY) });
  };

  const calculateOptimalFontSize = (element, newWidth, newHeight) => {
    const widthScale = newWidth / element.width;
    const heightScale = newHeight / element.height;
    const scale = Math.min(widthScale, heightScale);
    return Math.max(
      8,
      Math.min(72, Math.round((element.fontSize || 16) * scale))
    );
  };

  const handleResizeStop = (id, direction, ref, delta, position) => {
    const element = elements.find((el) => el.id === id);
    if (!element) return;
    const newWidth = parseInt(ref.style.width);
    const newHeight = parseInt(ref.style.height);
    const maxX = canvasDimensions.width - newWidth;
    const maxY = canvasDimensions.height - newHeight;
    const newX = Math.max(0, Math.min(position.x, maxX));
    const newY = Math.max(0, Math.min(position.y, maxY));
    updateElement(id, { width: newWidth, height: newHeight, x: newX, y: newY });
  };

  const handleResize = (id, direction, ref, delta, position) => {
    const element = elements.find((el) => el.id === id);
    if (!element || element.type !== "text") return;

    const newWidth = parseInt(ref.style.width);
    const newHeight = parseInt(ref.style.height);
    const tempFontSize = calculateOptimalFontSize(element, newWidth, newHeight);
    const elementNode = ref.querySelector("div");
    if (elementNode) {
      elementNode.style.fontSize = `${tempFontSize}px`;
    }
  };

  const renderElement = (element) => {
    const commonProps = {
      position: { x: Math.round(element.x), y: Math.round(element.y) },
      size: { width: element.width, height: element.height },
      bounds: "parent",
      scale,
      onDragStop: (e, d) => handleDragStop(element.id, e, d),
      onDrag: (e, d) => handleDrag(element.id, e, d),
      onResizeStop: (e, direction, ref, delta, position) =>
        handleResizeStop(element.id, direction, ref, delta, position),
      onResize:
        element.type === "text"
          ? (e, direction, ref, delta, position) =>
              handleResize(element.id, direction, ref, delta, position)
          : undefined,
      onClick: (e) => {
        e.stopPropagation();
        setSelectedElement(element.id);
      },
      style: {
        border:
          selectedElement === element.id
            ? "2px dashed #3b82f6"
            : "1px solid transparent",
        zIndex: selectedElement === element.id ? 1000 : 1,
        transition: "border 0.2s ease-in-out",
        cursor: "move",
        overflow: "visible",
        transform: `rotate(${element.rotation || 0}deg)`,
      },
      onMouseEnter: (e) => {
        if (selectedElement !== element.id)
          e.currentTarget.style.border = "1px dashed #999";
      },
      onMouseLeave: (e) => {
        if (selectedElement !== element.id)
          e.currentTarget.style.border = "1px solid transparent";
      },
    };

    switch (element.type) {
      case "image":
        return (
          <Rnd
            size={{ width: element.width, height: element.height }}
            position={{ x: Math.round(element.x), y: Math.round(element.y) }}
            lockAspectRatio={true}
            bounds="parent"
            scale={scale}
            onDrag={(e, d) => handleDrag(element.id, e, d)}
            onDragStop={(e, d) => handleDragStop(element.id, e, d)}
            onResizeStop={(e, direction, ref, delta, position) =>
              handleResizeStop(element.id, direction, ref, delta, position)
            }
            onClick={(e) => {
              e.stopPropagation();
              setSelectedElement(element.id);
            }}
            style={{
              border:
                selectedElement === element.id
                  ? "2px dashed #3b82f6"
                  : "1px solid transparent",
              zIndex: selectedElement === element.id ? 1000 : 1,
              transition: "border 0.2s ease-in-out",
              cursor: "move",
              overflow: "visible",
              transform: `rotate(${element.rotation || 0}deg)`,
            }}
          >
            <ImageElement element={element} updateElement={updateElement} />
          </Rnd>
        );

      case "text":
        return (
          <Rnd key={element.id} {...commonProps}>
            <TextElement
              element={element}
              dataBindingValue={dataBindingValue}
            />
          </Rnd>
        );
      case "shape":
        return (
          <Rnd key={element.id} {...commonProps}>
            <ShapeElement element={element} />
          </Rnd>
        );
      case "qr":
        return (
          <Rnd key={element.id} {...commonProps}>
            <QRCodeElement element={element} updateElement={updateElement} />
          </Rnd>
        );
      case "line":
        return (
          <Rnd key={element.id} {...commonProps}>
            <LineElement element={element} />
          </Rnd>
        );
      case "labels":
        return (
          <Rnd key={element.id} {...commonProps}>
            <IconsElement />
          </Rnd>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "white",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {isLoadingState ? (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "white" }} />
            <div style={{ color: "white" }}>Loading event data...</div>
          </Stack>
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              minWidth: 0,
              maxWidth: "100%",
            }}
            ref={containerRef}
          >
            <Box
              sx={{
                position: "relative",
                width: `${canvasDimensions.width * scale}px`,
                height: `${canvasDimensions.height * scale}px`,
                maxWidth: "100%",
                maxHeight: "100%",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  width: `${canvasDimensions.width}px`,
                  height: `${canvasDimensions.height}px`,
                }}
              >
                <Paper
                  ref={canvasRef}
                  elevation={8}
                  sx={{
                    position: "relative",
                    width: `${canvasDimensions.width}px`,
                    height: `${canvasDimensions.height}px`,
                    cursor: "pointer",
                    bgcolor: "#D1D1D1",
                  }}
                  onClick={() => setSelectedElement(null)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (
                      ["Delete", "Backspace"].includes(e.key) &&
                      selectedElement
                    )
                      e.preventDefault();
                  }}
                >
                  {/* --- Alignment Guides --- */}
                  {guides.map((g, i) => (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        background: "#000000",
                        zIndex: 2000,
                        ...(g.type === "vertical"
                          ? {
                              left: g.position,
                              top: 0,
                              width: "1px",
                              height: "100%",
                            }
                          : {
                              top: g.position,
                              left: 0,
                              height: "1px",
                              width: "100%",
                            }),
                      }}
                    />
                  ))}

                  {/* --- Elements --- */}
                  {elements.map(renderElement)}

                 
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
